import React, { useState, useEffect } from 'react';
import { useCreateTicket, useUpdateTicket, useUsers } from '../../hooks/useApi';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { FormField, FormGrid, Input, Select, Textarea } from '../ui/Form';

const FORM_ID = 'ticket-form';

const EMPTY = {
    title: '',
    description: '',
    priority: 'medium',
    status: 'open',
    category: 'Support',
    assignee_id: '',
};

const TicketModal = ({ isOpen, onClose, ticket }) => {
    const isEditMode = !!ticket;
    const [formData, setFormData] = useState(EMPTY);

    const { data: userResult } = useUsers({ limit: 1000 });
    const users = userResult?.data || [];
    const createTicket = useCreateTicket();
    const updateTicket = useUpdateTicket();

    useEffect(() => {
        if (ticket) {
            setFormData({
                title: ticket.title || '',
                description: ticket.description || '',
                priority: ticket.priority || 'medium',
                status: ticket.status || 'open',
                category: ticket.category || 'Support',
                assignee_id: ticket.assignee_id || '',
            });
        } else {
            setFormData(EMPTY);
        }
    }, [ticket, isOpen]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEditMode) {
                await updateTicket.mutateAsync({ id: ticket.id, data: formData });
            } else {
                await createTicket.mutateAsync(formData);
            }
            onClose();
        } catch (error) {
            console.error('Error saving ticket:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const isLoading = createTicket.isPending || updateTicket.isPending;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="lg"
            title={isEditMode ? 'Edit ticket' : 'New ticket'}
            subtitle={
                isEditMode
                    ? 'Update the details or reassign this request.'
                    : 'Describe the problem so the ICT team can act on it.'
            }
            footer={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button type="submit" form={FORM_ID} variant="primary" loading={isLoading}>
                        {isEditMode ? 'Save changes' : 'Create ticket'}
                    </Button>
                </>
            }
        >
            <form id={FORM_ID} onSubmit={handleSubmit} className="space-y-4">
                <FormField label="Ticket title" required htmlFor="ticket-title">
                    <Input
                        id="ticket-title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        required
                        placeholder="What needs attention?"
                    />
                </FormField>

                <FormField
                    label="Description"
                    htmlFor="ticket-description"
                    hint="Include what you expected, what happened, and any error message."
                >
                    <Textarea
                        id="ticket-description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows={4}
                    />
                </FormField>

                <FormGrid>
                    <FormField label="Priority" required htmlFor="ticket-priority">
                        <Select
                            id="ticket-priority"
                            name="priority"
                            value={formData.priority}
                            onChange={handleChange}
                        >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="critical">Critical</option>
                        </Select>
                    </FormField>

                    <FormField label="Assignee" htmlFor="ticket-assignee">
                        <Select
                            id="ticket-assignee"
                            name="assignee_id"
                            value={formData.assignee_id}
                            onChange={handleChange}
                        >
                            <option value="">Unassigned</option>
                            {users.map((u) => (
                                <option key={u.id} value={u.id}>
                                    {u.first_name} {u.last_name}
                                </option>
                            ))}
                        </Select>
                    </FormField>

                    <FormField label="Category" htmlFor="ticket-category">
                        <Select
                            id="ticket-category"
                            name="category"
                            value={formData.category}
                            onChange={handleChange}
                        >
                            <option value="Support">Support</option>
                            <option value="Bug">Bug</option>
                            <option value="Feature Request">Feature request</option>
                            <option value="Technical">Technical</option>
                            <option value="Other">Other</option>
                        </Select>
                    </FormField>

                    {isEditMode && (
                        <FormField label="Status" required htmlFor="ticket-status">
                            <Select
                                id="ticket-status"
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                            >
                                <option value="open">Open</option>
                                <option value="in_progress">In progress</option>
                                <option value="resolved">Resolved</option>
                                <option value="closed">Closed</option>
                            </Select>
                        </FormField>
                    )}
                </FormGrid>
            </form>
        </Modal>
    );
};

export default TicketModal;
